import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Laptop } from 'lucide-react';

import { FrFlag, GbFlag, getFlag } from '@/components/flags';

import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import LogoLight from '@/assets/images/logo_smsreflex_light.png';
import LogoDark from '@/assets/images/logo_smsreflex_dark.png';

type AuthLayoutProps = {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Hide the Card chrome, useful for status pages (Logout, ErrorPage) */
  bare?: boolean;
};

function AuthTopbar() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useTheme();
  const currentLang =
    i18n.language === 'fr' || i18n.language === 'en' ? i18n.language : 'en';
  // Map currentLang -> flag code (en uses the GB flag by web convention).
  const CurrentFlag = getFlag(currentLang === 'en' ? 'gb' : currentLang);

  return (
    <div className="absolute right-4 top-4 flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5">
            <span className="inline-flex h-4 w-4 flex-shrink-0 overflow-hidden rounded-full ring-1 ring-border">
              <CurrentFlag className="h-full w-full" />
            </span>
            <span className="text-xs font-semibold uppercase">{currentLang}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => i18n.changeLanguage('en')}>
            <span className="mr-2 inline-flex h-4 w-4 flex-shrink-0 overflow-hidden rounded-full ring-1 ring-border">
              <GbFlag className="h-full w-full" />
            </span>
            English
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => i18n.changeLanguage('fr')}>
            <span className="mr-2 inline-flex h-4 w-4 flex-shrink-0 overflow-hidden rounded-full ring-1 ring-border">
              <FrFlag className="h-full w-full" />
            </span>
            Français
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Toggle theme">
            {theme === 'dark' ? (
              <Moon className="h-4 w-4" />
            ) : theme === 'light' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Laptop className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setTheme('light')}>
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('dark')}>
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('system')}>
            <Laptop className="mr-2 h-4 w-4" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function AuthLayout({ title, description, children, bare = false }: AuthLayoutProps) {
  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-muted/40 via-background to-muted/40 p-4">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:radial-gradient(hsl(var(--primary))_1px,transparent_1px)] [background-size:24px_24px]"
        aria-hidden="true"
      />
      <AuthTopbar />
      {bare ? (
        <div className="relative z-10 w-full max-w-md text-center">{children}</div>
      ) : (
        <Card className="relative z-10 w-full max-w-md shadow-xl shadow-foreground/5">
          <CardHeader className="space-y-3 text-center">
            <img src={LogoLight} alt="SMSReflex" className="mx-auto h-12 w-auto dark:hidden" />
            <img src={LogoDark} alt="SMSReflex" className="mx-auto hidden h-12 w-auto dark:block" />
            {title && <CardTitle className="text-2xl">{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      )}
    </div>
  );
}
